import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { EvaluationCriteria, EvaluationCriteriaInput } from '@/types/employeeEvaluations';

export const useEvaluationCriteria = (brandId: string | null) => {
  const [criteria, setCriteria] = useState<EvaluationCriteria[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCriteria = useCallback(async () => {
    if (!brandId) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('os_employee_evaluation_criteria')
        .select(`
          *,
          category:os_employee_evaluation_categories(*)
        `)
        .eq('brand_id', brandId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setCriteria((data || []) as EvaluationCriteria[]);
    } catch (error: any) {
      toast.error('Failed to load evaluation criteria');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    fetchCriteria();
  }, [fetchCriteria]);

  const createCriteria = async (input: EvaluationCriteriaInput): Promise<EvaluationCriteria | null> => {
    if (!brandId) return null;
    try {
      const { data, error } = await supabase
        .from('os_employee_evaluation_criteria')
        .insert({ ...input, brand_id: brandId })
        .select(`*, category:os_employee_evaluation_categories(*)`)
        .single();

      if (error) throw error;
      toast.success('Criteria created');
      await fetchCriteria();
      return data as EvaluationCriteria;
    } catch (error: any) {
      toast.error(error.message || 'Failed to create criteria');
      return null;
    }
  };

  const updateCriteria = async (id: string, input: Partial<EvaluationCriteriaInput>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('os_employee_evaluation_criteria')
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      toast.success('Criteria updated');
      await fetchCriteria();
      return true;
    } catch (error: any) {
      toast.error(error.message || 'Failed to update criteria');
      return false;
    }
  };

  const deleteCriteria = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('os_employee_evaluation_criteria')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Criteria deleted');
      await fetchCriteria();
      return true;
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete criteria');
      return false;
    }
  };

  const initializeDefaultCriteria = async (categories: { id: string; code: string }[]): Promise<boolean> => {
    if (!brandId || categories.length === 0) return false;
    try {
      const hsCategory = categories.find(c => c.code === 'HS');
      const ssCategory = categories.find(c => c.code === 'SS');

      if (!hsCategory || !ssCategory) {
        toast.error('Please initialize categories first');
        return false;
      }

      const defaultCriteria = [
        { code: 'CAT', name: 'Completed assignments on time', description: 'Delivers work by agreed deadlines', category_id: hsCategory.id, weight: 3, sort_order: 1 },
        { code: 'PAW', name: 'Produced the expected amount of work', description: 'Meets productivity expectations', category_id: ssCategory.id, weight: 5, sort_order: 2 },
        { code: 'PWQ', name: 'Produced work at the expected quality', description: 'Work meets quality standards', category_id: hsCategory.id, weight: 4, sort_order: 3 },
        { code: 'DOC', name: 'Documented well his/her work', description: 'Creates clear documentation', category_id: hsCategory.id, weight: 2, sort_order: 4 },
        { code: 'FMD', name: "Followed management's direction", description: 'Aligns with leadership guidance', category_id: ssCategory.id, weight: 4, sort_order: 5 },
        { code: 'HOI', name: 'Helped others improve', description: 'Mentors and supports teammates', category_id: ssCategory.id, weight: 3, sort_order: 6 },
        { code: 'COM', name: 'Communicated well', description: 'Clear and effective communication', category_id: ssCategory.id, weight: 3, sort_order: 7 },
        { code: 'CRI', name: 'Responded well to times of crisis', description: 'Handles pressure effectively', category_id: ssCategory.id, weight: 2, sort_order: 8 },
        { code: 'MGI', name: "Manager's general impression", description: 'Overall performance perception', category_id: ssCategory.id, weight: 4, sort_order: 9 },
      ];

      for (const crit of defaultCriteria) {
        const { error } = await supabase
          .from('os_employee_evaluation_criteria')
          .upsert({ ...crit, brand_id: brandId }, { onConflict: 'brand_id,code' });
        if (error) console.error('Error upserting criteria:', error);
      }

      await fetchCriteria();
      toast.success('Default criteria initialized');
      return true;
    } catch (error: any) {
      toast.error('Failed to initialize criteria');
      return false;
    }
  };

  return {
    criteria,
    isLoading,
    refetch: fetchCriteria,
    createCriteria,
    updateCriteria,
    deleteCriteria,
    initializeDefaultCriteria,
  };
};
